// Generic machine-case modal: overlay backdrop, close button, Esc, focus trap,
// inert background. Drives both the help dialog and the 软盘文件 manager.
export class ModalDialog {
  constructor({ trigger, overlay, background, onOpen }) {
    this.trigger = trigger;
    this.overlay = overlay;
    this.background = background;
    this.onOpen = onOpen;
    this.dialog = overlay?.querySelector('[role="dialog"]');
    this.closeButton = overlay?.querySelector('[data-dialog-close]');
    this.returnFocus = null;
    this.backgroundWasInert = false;

    if (!this.trigger || !this.overlay || !this.dialog || !this.closeButton) {
      throw new Error('Dialog markup is incomplete');
    }

    this.trigger.addEventListener('click', () => this.open());
    this.closeButton.addEventListener('click', () => this.close());
    this.overlay.addEventListener('pointerdown', (event) => {
      if (event.target === this.overlay) this.close();
    });
    window.addEventListener('keydown', (event) => this._onKeydown(event), true);
  }

  get isOpen() {
    return !this.overlay.hidden;
  }

  open() {
    if (this.isOpen) return;
    this.returnFocus = document.activeElement;
    if (this.background) {
      this.backgroundWasInert = this.background.inert;
      this.background.inert = true;
    }
    this.overlay.hidden = false;
    document.documentElement.classList.add('help-open');
    if (this.onOpen) this.onOpen();
    requestAnimationFrame(() => this.closeButton.focus({ preventScroll: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.overlay.hidden = true;
    document.documentElement.classList.remove('help-open');
    if (this.background) this.background.inert = this.backgroundWasInert;

    const target = this.returnFocus?.isConnected ? this.returnFocus : this.trigger;
    requestAnimationFrame(() => target.focus({ preventScroll: true }));
  }

  _focusableElements() {
    return [...this.dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
      'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
  }

  _onKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      // An editable field inside the dialog owns its own Escape (e.g. the disk
      // manager's inline-rename cancel); this listener runs in the capture
      // phase, so stand aside or the whole dialog would close instead.
      const t = event.target;
      if (t && this.dialog.contains(t) &&
          (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = this._focusableElements();
    if (focusable.length === 0) {
      event.preventDefault();
      this.dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const current = document.activeElement;
    if (event.shiftKey && (current === first || !this.dialog.contains(current))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
